import type {
  ContractArticleSuggestionsQueryParams,
  ContractArticleSuggestionsResponse,
} from './types';

import { ENDPOINT } from '~/constants/endpoints';

import { type KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { withoutObjectKeysWhereValueIs } from '@vestigas/utils';

import {
  zodContractArticleSuggestionsQueryParamsSchema,
  zodContractArticleSuggestionsResponseSchema,
} from './validations';

/**
 * Fetches articles the caller's company has previously used on contracts, to
 * prefill a contract item row instead of retyping it. `sellerCompanyId` /
 * `buyerCompanyId` only boost matching articles to the top of the ranking;
 * they never exclude, so results are still returned for a new counterparty.
 *
 * @param queryParams - Optional typeahead query, party ids to boost, a contract to exclude from the history, and a result limit (1–50, default 10).
 * @param options - Additional request options (e.g. abort signal).
 * @returns The ranked article suggestions.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Contract-Suggestions/operation/get_contract_article_suggestions_contracts_suggestions_articles_get
 */
export const fetchContractArticleSuggestions = async (
  queryParams: Partial<ContractArticleSuggestionsQueryParams>,
  options?: KyRequestOptions,
) => {
  return await vestigasApi
    .get(ENDPOINT.CONTRACT.GET_ARTICLE_SUGGESTIONS(), {
      searchParams: queryParams,
      ...options,
      validate: {
        ...options?.validate,
        response: zodContractArticleSuggestionsResponseSchema,
        searchParams: zodContractArticleSuggestionsQueryParamsSchema.transform(
          (data) => withoutObjectKeysWhereValueIs(data, [undefined, '']),
        ),
      },
    })
    .json<ContractArticleSuggestionsResponse>();
};
